import React, { useState } from "react";
import { Copy, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/** Return addresses to register in Microsoft Entra, and a one-click connection check. */
export function SsoReturnAddresses({ data }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const currentReturn = data.current || "Not configured";
  const copy = (value) => navigator.clipboard?.writeText(value).then(() => toast.success("Copied"));
  const test = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/admin/sso/test"); setResult(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="sso-return-addresses">
      <div className="eyebrow mb-1">Production sign-in</div>
      <h3 className="font-heading font-bold text-xl mb-2" style={{ color: "var(--ink)" }}>Microsoft return addresses</h3>
      <p className="text-sm mb-4 max-w-3xl" style={{ color: "var(--muted)" }}>
        Keep the current temporary or rehearsal address registered with both approved production
        addresses below throughout validation.
      </p>
      <div
        className="border px-3 py-3 mb-4"
        style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        data-testid="sso-current-return-address"
      >
        <div className="eyebrow text-[9px] mb-1">Current temporary or rehearsal address</div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <code
            className="font-mono text-xs px-2 py-1 border"
            style={{ borderColor: "var(--border)" }}
            data-testid="sso-current-return"
          >
            {currentReturn}
          </code>
          <button
            type="button"
            className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1"
            onClick={() => copy(data.current)}
            disabled={!data.current}
            data-testid="sso-current-return-copy-btn"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      </div>
      <ul className="space-y-2 mb-3">
        {data.production.map((url, index) => (
          <li key={url} className="flex flex-wrap items-center gap-2 text-sm">
            <code className="font-mono text-xs px-2 py-1 border" style={{ borderColor: "var(--border)" }} data-testid="sso-return-address">{url}</code>
            <button type="button" className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={() => copy(url)} data-testid={`sso-return-copy-btn-${index}`}><Copy size={12} /> Copy</button>
          </li>
        ))}
      </ul>
      <div className="text-xs mb-4" style={{ color: "var(--muted)" }}>
        Register all three exact addresses concurrently. After the stability window, retire the
        temporary address only when no approved rollback still depends on it.
      </div>
      <button type="button" className="btn-primary flex items-center gap-2" disabled={busy} onClick={test} data-testid="sso-test-signin-btn"><PlayCircle size={14} /> {busy ? "Testing…" : "Test Microsoft sign-in"}</button>
      {result && (
        <div className="mt-3 text-sm" style={{ color: result.ok ? "#0F5132" : "#842029" }} data-testid="sso-test-result">{result.message}</div>
      )}
    </section>
  );
}
